<template>
	<base-modal
		name="newPlan"
		:title="$t('shop.newPlan.title')"
		:subtitle="$t('shop.newPlan.recommendation')"
		:save-text="$t('shop.newPlan.button.create')"
		:loading="loading"
		@submit="createPlan($event)"
	>
		<!-- Name des Plans -->
		<div class="text-body-2 font-weight-medium mb-2">
			{{ $t('shop.newPlan.label.name') }}
		</div>
		<v-text-field
			name="name"
			v-model="name"
			:placeholder="$t('shop.newPlan.placeholder.name')"
			:rules="planNameRules"
			counter="30"
			outlined
			dense
			clearable
			required
			hide-details="auto"
		></v-text-field>

		<!-- Beschreibung des Plans -->
		<div class="text-body-2 font-weight-medium mb-2 mt-4">
			{{ $t('shop.newPlan.label.description') }}
		</div>
		<v-textarea
			name="description"
			v-model="description"
			:placeholder="$t('shop.newPlan.placeholder.description')"
			:rules="planDescriptionRules"
			counter="500"
			outlined
			dense
			clearable
			required
			hide-details="auto"
		></v-textarea>

		<div class="text-h6 font-weight-bold mt-6 mb-2">
			{{ $t('shop.newPlan.section.priceDetails') }}
		</div>

		<!-- Preis des Plans -->
		<div class="text-body-2 font-weight-medium mb-2">
			{{ $t('shop.newPlan.label.price') }}
		</div>
		<v-text-field
			name="price"
			v-model="price"
			:placeholder="$t('shop.newPlan.placeholder.price')"
			:rules="priceRules"
			type="number"
			min="0"
			inputmode="decimal"
			outlined
			dense
			clearable
			required
			hide-details="auto"
		></v-text-field>

		<!-- Laufzeit des Plans: ein Monat oder ein Jahr -->
		<div class="text-body-2 font-weight-medium mb-2 mt-4">
			{{ $t('shop.newPlan.label.paymentPeriod') }}
		</div>
		<v-select
			v-model="period"
			:items="periodOptions"
			name="period"
			outlined
			dense
			required
			hide-details="auto"
		></v-select>

		<div class="text-body-2 font-weight-medium mb-2 mt-4">
			{{ $t('shop.newPlan.label.trialPeriod') }}
		</div>
		<v-select
			v-model="trialVariation"
			:items="trialOptions"
			name="trial"
			outlined
			dense
			hide-details="auto"
		></v-select>

		<!-- Der Testzeitraum traegt einen Preis. Ohne Wunsch ist es der regulaere
		     Planpreis: ein Zeitraum ohne Preis waere fuer jeden Kaeufer kostenlos,
		     der den Zyklus nicht ueberschreibt. -->
		<v-text-field
			v-if="trialVariation !== 'Kein Testzeitraum'"
			:label="$t('shop.newPlan.label.trialPrice')"
			name="trial_price"
			v-model="trialPrice"
			:placeholder="$t('shop.newPlan.placeholder.trialPrice')"
			:rules="priceRules"
			outlined
			dense
		></v-text-field>

		<div class="text-h6 font-weight-bold mt-6 mb-2">
			{{ $t('shop.newPlan.section.visibility') }}
		</div>
		<div class="d-flex justify-space-between align-center">
			<div>
				<span class="text-body-2">{{ $t('shop.newPlan.label.activatePlan') }}</span>
				<v-icon
					v-if="activePlan == true"
					class="green--text pl-2"
					size="18"
				>mdi-check</v-icon>
			</div>
			<div class="d-flex align-center">
				<span class="pr-4 grey--text text-body-2">{{ $t('shop.newPlan.noYes') }}</span>
				<v-switch
					v-model="activePlan"
					hide-details
					class="mt-0"
				></v-switch>
			</div>
		</div>
	</base-modal>
</template>

<script>

	export default {
		name: 'newPlan',

		components: {
			BaseModal: () => import('@/components/modal/baseModal')
		},

		data(){
			return {
				name: '',
				description: '',
				price: '',
				activePlan: true,
				// Laufzeit: Monat = 1 Zyklus, Jahr = 12 Zyklen
				period: 'Monatlich',
				periodOptions: ['Monatlich', 'Jährlich'],
				// Testzeitraum: zurzeit auskommentiert
				trialVariation: 'Kein Testzeitraum',
				trialOptions: ['Kein Testzeitraum', 'Woche', 'Monat'],
				trialPrice: '',
				planNameRules: [
					v => !!v || this.$t('shop.newPlan.validation.nameRequired'),
					v => (v && v.length <= 30) || this.$t('shop.newPlan.validation.nameMaxLength'),
					v => (v && v.length >= 2) || this.$t('shop.newPlan.validation.nameMinLength')
				],
				planDescriptionRules: [
					v => !!v || this.$t('shop.newPlan.validation.descriptionRequired'),
					v => (v && v.length <= 500) || this.$t('shop.newPlan.validation.descriptionMaxLength'),
					v => (v && v.length >= 2) || this.$t('shop.newPlan.validation.descriptionMinLength')
				],
				priceRules: [
					v => !!v || this.$t('shop.newPlan.validation.priceRequired'),
					v => !isNaN(parseFloat(v)) && !isNaN(v - 0) || this.$t('shop.newPlan.validation.priceFormat'),
					v => v >= 0 || this.$t('shop.newPlan.validation.priceNegative')
				],
				loading: false
			}
		},

		methods: {
			currentUser() {
				return this.$store.state.auth.status.loggedIn;
			},

			createPlan(e){
				this.loading = true;

				const event = {
					name: this.name,
					description: this.description,
					price: this.price.replace(',', '.'),
					period: this.period,
					currency: 'EUR',
					active: this.activePlan,
				};

				// Der Testzeitraum traegt in JEDEM Fall einen Preis: bei einem Wunsch
				// den angegebenen, sonst den regulaeren Planpreis. Ein Zeitraum ohne
				// Preis waere fuer jeden Kaeufer kostenlos, der ihn nicht ueberschreibt.
				event.trial_price = this.trialVariation === 'Kein Testzeitraum'
					? this.price.replace(',', '.')
					: this.trialPrice.replace(',', '.');

				if (this.trialVariation !== 'Kein Testzeitraum') {
					event.trial = this.trialVariation;
				}

				this.$store.dispatch('post',
					{
						endpoint: '/api/v1/plans',
						state: 'plans',
						event
					}
				)
				.then(() => {
					this.$store.dispatch('fetch', { endpoint: '/api/v1/plans', state: 'plans'})
					this.loading = false
					this.$store.dispatch("modal/close", 'newPlan')
				}).catch((error) => {
					this.loading = false
				})
			},
		}
		// Ende Methods
	}
</script>
